
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { AuthFormMode } from '@/lib/types';

export const useAuth = (mode: AuthFormMode) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Function to add user to MailerLite with improved error handling
  const addUserToMailerLite = async (email: string, name: string) => {
    try {
      console.log(`Attempting to add user to MailerLite: ${email}, ${name || "No name provided"}`);
      
      const { data, error } = await supabase.functions.invoke('mailerlite-subscribe', {
        body: { email, name }
      });
      
      if (error) {
        console.error('Error invoking mailerlite-subscribe function:', error);
        return;
      } 
      
      console.log('Response from mailerlite-subscribe:', data);
      
      if (data.success) {
        console.log('User successfully added to MailerLite');
        
        if (!data.subscriberInGroup) {
          console.warn('User was added to MailerLite but may not have been assigned to the New Leads group');
        }
      } else {
        console.error('Failed to add user to MailerLite:', data.error || 'Unknown error');
      }
    } catch (error) {
      console.error('Exception when adding user to MailerLite:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    setAuthError(null);
    
    try {
      if (mode === 'login') {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;

        toast({
          title: 'Logged in successfully',
          description: "Checking your permissions..."
        });
        
        // Check if user has admin role
        try {
          const { data: isAdmin, error: adminCheckError } = await supabase.rpc('has_role', {
            role: 'admin'
          });
          
          if (adminCheckError) {
            console.error('Error checking admin status:', adminCheckError);
          }
          
          if (isAdmin) {
            // User has admin role, redirect to admin page
            toast({
              title: 'Admin access granted',
              description: "Redirecting to admin dashboard"
            });
            navigate('/admin', { replace: true, state: { fromLogin: true } });
            return;
          }
          
          // If not admin, continue with regular subscription check
          const { data: subscription, error: subscriptionError } = await supabase
            .from('subscriptions')
            .select('*')
            .eq('user_id', data.user.id)
            .eq('status', 'active')
            .maybeSingle();
          
          console.log('Subscription check on login:', subscription);
          
          if (subscriptionError && subscriptionError.code !== 'PGRST116') {
            console.error('Error checking subscription during login:', subscriptionError);
          }
          
          if (subscription) {
            // User has active subscription, redirect to dashboard
            toast({
              title: 'Access granted',
              description: "Welcome to your dashboard!"
            });
            navigate('/dashboard', { replace: true, state: { fromLogin: true } });
          } else {
            // No active subscription, redirect to pricing
            toast({
              title: 'Subscription required',
              description: "Please select a plan to access the dashboard.",
              variant: "destructive"
            });
            navigate('/pricing', { replace: true });
          }
        } catch (subError) {
          console.error('Unexpected error during subscription check:', subError);
          navigate('/pricing', { replace: true });
        }
      } else {
        // For registration, we don't need email verification anymore
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: name,
            },
          },
        });

        if (error) throw error;
        
        // Add the user to MailerLite after successful registration
        if (data.user) {
          console.log('Registration successful, adding user to MailerLite');
          await addUserToMailerLite(email, name);
        }
        
        toast({
          title: 'Account created successfully',
          description: "You're being redirected to select a plan."
        });
        
        // Redirect directly to pricing page after signup
        navigate('/pricing', { replace: true });
      }
    } catch (error: any) {
      console.error('Authentication error:', error);
      setAuthError(error.message || 'Please check your credentials and try again.');
      toast({
        title: 'Authentication failed',
        description: error.message || 'Please check your credentials and try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    name,
    setName,
    isLoading,
    isSubmitting,
    authError,
    handleSubmit
  };
};
